import uniq from 'lodash/uniq'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import intersection from 'lodash/intersection'
import {
  Dependants,
  DependantValues,
  DependenciesMap,
  EditorContentfulConfiguration,
  OptionSelection,
  ProductVariant,
  PropertyValuesWithDependants,
  SelectionState,
  SelectorConfiguration,
} from '../../types'
import { systemProperties } from '../../constants'

function extractValuesSet(collection, selectorName) {
  return uniq(
    collection
      .map((item) => {
        return item[selectorName]
      })
      .filter(Boolean)
  ).sort(
    new Intl.Collator('en', { numeric: true, sensitivity: 'accent' }).compare
  )
}

// For dependency, never include quantity as it's the last thing need to be considered

export function extractDeps(collection: ProductVariant[]): DependenciesMap {
  // First, extract all option variant names across this collection
  const variantNames = [
    ...collection.reduce((variantNamesSet, item) => {
      Object.keys(item).forEach((key) => variantNamesSet.add(key))
      return variantNamesSet
    }, new Set<string>()),
  ].filter((prop) => !systemProperties.includes(prop.trim()))

  // Extract list of values per variantNames
  const fullValuesPerVariantName = [...variantNames].reduce(
    (fullValuesMapping, name) => {
      fullValuesMapping[name] = {
        // fullValuesMapping[name].values
        values: extractValuesSet(collection, name),
      }
      return fullValuesMapping
    },
    {}
  )

  // Now for each of variant name, for each of variant value, filtered the collection and check again the fullValues
  //  the smaller one mean there will be some dependencies between them
  const deps = {}

  for (const variantName of variantNames) {
    deps[variantName] = {}

    for (const variantValue of fullValuesPerVariantName[variantName].values) {
      const filteredOptionSales = collection.filter(
        (productOption) => productOption[variantName] === variantValue
      )
      if (isEmpty(filteredOptionSales)) {
        continue
      }

      const variantValuesDeps = {}

      // Now build dependencies
      for (const subVariantName of variantNames) {
        if (subVariantName === variantName) {
          continue
        }

        variantValuesDeps[subVariantName] = extractValuesSet(
          filteredOptionSales,
          subVariantName
        )
      }

      // Add deps
      // size: 150x150 has (color: 1), size: 300x300 has (color: [1,2])
      deps[variantName][variantValue] = variantValuesDeps
    }
  }

  return deps
}

export function getDefaultSelection(
  productVariantsSales: ProductVariant[],
  selectors: SelectorConfiguration[]
): OptionSelection[] {
  // Get the cheapest one (by "per unit" price)
  const selection = productVariantsSales.sort(
    (a, b) =>
      (b.salePrice || b.price) / +b.quantity -
      (a.salePrice || a.price) / +a.quantity
  )?.[productVariantsSales?.length - 1]

  // Now sort the selection follow selectors as default one
  return selectors
    .map((selector) => {
      if (!selection[selector.name]) {
        // No value
        return null
      }

      return {
        name: selector.name,
        value: String(selection[selector.name] ?? ''),
      }
    })
    .filter(Boolean)
}

function intersectValues(
  intersectionValues: Dependants,
  newIntersectionValues: Dependants
): Dependants {
  const result: Dependants = {}
  if (isEmpty(intersectionValues)) {
    return newIntersectionValues ?? {}
  }

  if (isEmpty(newIntersectionValues)) {
    return intersectionValues ?? {}
  }

  // For each key in intersectionValues, look up the values
  for (const selectorName of Object.keys(intersectionValues)) {
    const currentValues = intersectionValues[selectorName] ?? []
    const newValues = newIntersectionValues[selectorName]
    if (!newValues) {
      // Undefined for newValues mean this prop is not defined in new, just keep the old one
      result[selectorName] = currentValues
      continue
    }

    result[selectorName] = intersection(currentValues, newValues)
  }

  return result
}

function firstValueOf(
  intersectionValues: Dependants,
  name: string
): string | undefined {
  const values: DependantValues = intersectionValues[name]

  return isEmpty(values) ? undefined : values[0]
}

export function getSelectorsConfiguration({
  values,
  editorConfiguration: {
    fieldsConfiguration: editorFieldsVisualConfiguration = [],
  },
  dependencies,
}: {
  values: OptionSelection[]
  editorConfiguration: EditorContentfulConfiguration
  dependencies: DependenciesMap
}): SelectorConfiguration[] {
  function selectedValueOf(optionName: string): string {
    return values.find((selection) => selection.name === optionName)?.value
  }

  const selectors: SelectorConfiguration[] = []
  let intersectionValues: Dependants = {} // Empty dependants
  let optionValues: string[] = []

  if (isEmpty(editorFieldsVisualConfiguration)) {
    // Initial state
    return []
  }

  // Should aggregate and get the intersection amount of values
  for (const fieldConfig of editorFieldsVisualConfiguration) {
    const dependencyOptionValues: PropertyValuesWithDependants =
      dependencies[fieldConfig.name] || {}
    const selectedValue = selectedValueOf(fieldConfig.name)
    let intersectionResult: Dependants = intersectionValues

    if (isEmpty(intersectionValues)) {
      // Get option values of the main property
      optionValues = Object.keys(dependencyOptionValues ?? {})
      intersectionResult =
        dependencyOptionValues[selectedValue ?? optionValues[0]]
    } else {
      const selectedValue =
        selectedValueOf(fieldConfig.name) ??
        firstValueOf(intersectionValues, fieldConfig.name)

      // Not exist, skip it
      if (!selectedValue) {
        continue
      }

      const newIntersectionValues = dependencyOptionValues[selectedValue] // Can be null
      // Intersect values with existing one first and extract it
      intersectionResult = intersectValues(
        intersectionValues,
        newIntersectionValues
      )
      optionValues = intersectionResult[fieldConfig.name] ?? []
    }

    if (isEmpty(optionValues)) {
      // Field visibility configuration contains invalid selector, just skip
      continue
    }
    const selectorConfiguration = {
      ...fieldConfig,
      values: optionValues,
    }

    selectors.push(selectorConfiguration)

    intersectionValues = intersectionResult
  }

  return selectors
}

function filterProductOptionSalesBySelectedVariants({
  productVariantsSales,
  selectedVariants,
}: {
  productVariantsSales: SelectionState['productVariantsSales']
  selectedVariants: SelectionState['values']
}): SelectionState['productVariantsSales'] {
  let filteredOptionSales = productVariantsSales
  for (const selectionValue of selectedVariants) {
    const { name, value } = selectionValue
    const selectorFilteredOptionSales = filteredOptionSales.filter(
      (productOption) => isEqual(productOption[name], value)
    )
    if (!isEmpty(selectorFilteredOptionSales)) {
      filteredOptionSales = selectorFilteredOptionSales
    }
  }

  return filteredOptionSales
}

/**
 * This value will extract product option based on the values of option selected
 * which is different from **getDefaultSelection** which return selectors with default values
 *
 * @param values
 * @param productVariantsSales
 */
export function getProductOptionSelected({
  values,
  productVariantsSales,
}: Pick<SelectionState, 'values' | 'productVariantsSales'>): {
  selection: ProductVariant
  isUnique: boolean
} {
  const filteredOptionSales = filterProductOptionSalesBySelectedVariants({
    productVariantsSales,
    selectedVariants: values,
  })

  // Now we have the product option selection. Should always be. Otherwise, it's an error
  const selection = filteredOptionSales[0]
  return { selection, isUnique: filteredOptionSales?.length === 1 }
}

export const getProductOptionsWithSalesData = (state) => {
  const selectors = getSelectorsConfiguration(state)
  const noQuantitySelectorsNames = selectors
    .map((selector) => selector.name !== 'quantity' && selector.name)
    .filter(Boolean)
  const filteredOptionSales = filterProductOptionSalesBySelectedVariants({
    productVariantsSales: state.productVariantsSales,
    selectedVariants: state.values.filter((value) =>
      noQuantitySelectorsNames.includes(value.name)
    ),
  })

  const possibleQuantityValues = selectors.find(
    (value) => value.name === 'quantity'
  ).values

  const result = possibleQuantityValues
    .map((value) =>
      filteredOptionSales.find((option) => option.quantity === value)
    )
    .filter(Boolean)

  return result
}
